function safeTrim(value) {
  if (value === undefined || value === null) {
    return '';
  }
  return String(value).trim();
}

function getToolName(tool) {
  if (tool === undefined || tool === null) return '';
  if (typeof tool.toolName === 'string' && tool.toolName.trim() !== '') return tool.toolName.trim();
  if (typeof tool.toolname === 'string' && tool.toolname.trim() !== '') return tool.toolname.trim();
  if (typeof tool.name === 'string' && tool.name.trim() !== '') return tool.name.trim();
  return '';
}

function getToolData(tool) {
  if (tool === undefined || tool === null || typeof tool !== 'object') return {};
  if (tool.toolData !== undefined && tool.toolData !== null && typeof tool.toolData === 'object') return tool.toolData;
  if (tool.tooldata !== undefined && tool.tooldata !== null && typeof tool.tooldata === 'object') return tool.tooldata;
  return {};
}

const collectionMetaById = {
  aiTools: { id: 'aiTools', name: 'Built-in tools', logo: '/integrations/aiTools.svg' },
  custom: { id: 'custom', name: 'Custom tools', logo: '/integrations/customTools.svg' },
  microsoftOutlook: { id: 'microsoftOutlook', name: 'Microsoft Outlook', logo: '/integrations/outlook.svg' },
  gmail: { id: 'gmail', name: 'Gmail', logo: '/integrations/gmail.svg' },
  resend: { id: 'resend', name: 'Resend', logo: '/integrations/resend.svg' },
  slack: { id: 'slack', name: 'Slack', logo: '/integrations/slack.svg' },
  monday: { id: 'monday', name: 'Monday.com', logo: '/integrations/monday.svg' },
  github: { id: 'github', name: 'GitHub', logo: '/integrations/github.svg' },
  stripe: { id: 'stripe', name: 'Stripe', logo: '/integrations/stripe.svg' },
  notion: { id: 'notion', name: 'Notion', logo: '/integrations/notion.svg' },
  googleDrive: { id: 'googleDrive', name: 'Google Drive', logo: '/integrations/googleDrive.svg' },
  zoom: { id: 'zoom', name: 'Zoom', logo: '/integrations/zoom.svg' },
  salesforce: { id: 'salesforce', name: 'Salesforce', logo: '/integrations/salesforce.svg' },
  zendesk: { id: 'zendesk', name: 'Zendesk', logo: '/integrations/zendesk.svg' },
  twilio: { id: 'twilio', name: 'Twilio', logo: '/integrations/twilio.svg' },
  serviceNow: { id: 'serviceNow', name: 'ServiceNow', logo: '/integrations/serviceNow.svg' },
  dynamics365: { id: 'dynamics365', name: 'Dynamics 365', logo: '' },
  powerBi: { id: 'powerBi', name: 'Power BI', logo: '' },
  hubspot: { id: 'hubspot', name: 'HubSpot', logo: '' },
  microsoftTeams: { id: 'microsoftTeams', name: 'Microsoft Teams', logo: '' },
  sharePoint: { id: 'sharePoint', name: 'SharePoint', logo: '' },
  azureDevOps: { id: 'azureDevOps', name: 'Azure DevOps', logo: '' },
  sap: { id: 'sap', name: 'SAP', logo: '' },
  workday: { id: 'workday', name: 'Workday', logo: '' }
};

function inferCollectionIdFromToolName(toolName) {
  const name = safeTrim(toolName).toLowerCase();
  if (name.startsWith('microsoft outlook') === true) return 'microsoftOutlook';
  if (name.startsWith('gmail') === true) return 'gmail';
  if (name.startsWith('resend') === true) return 'resend';
  if (name.startsWith('slack') === true) return 'slack';
  if (name.startsWith('monday') === true) return 'monday';
  if (name.startsWith('github') === true) return 'github';
  if (name.startsWith('stripe') === true) return 'stripe';
  if (name.startsWith('notion') === true) return 'notion';
  if (name.startsWith('google drive') === true) return 'googleDrive';
  if (name.startsWith('zoom') === true) return 'zoom';
  if (name.startsWith('salesforce') === true) return 'salesforce';
  if (name.startsWith('zendesk') === true) return 'zendesk';
  if (name.startsWith('twilio') === true) return 'twilio';
  if (name.startsWith('servicenow') === true || name.startsWith('service now') === true) return 'serviceNow';
  if (name.startsWith('dynamics 365') === true || name.startsWith('dynamics365') === true || name.startsWith('dataverse') === true) return 'dynamics365';
  if (name.startsWith('power bi') === true || name.startsWith('powerbi') === true) return 'powerBi';
  if (name.startsWith('hubspot') === true) return 'hubspot';
  if (name.startsWith('microsoft teams') === true || name.startsWith('teams -') === true) return 'microsoftTeams';
  if (name.startsWith('sharepoint') === true) return 'sharePoint';
  if (name.startsWith('azure devops') === true) return 'azureDevOps';
  if (name.startsWith('sap -') === true || name.startsWith('sap ') === true) return 'sap';
  if (name.startsWith('workday') === true) return 'workday';
  return '';
}

export function inferToolCollectionId(tool) {
  const toolData = getToolData(tool);

  const explicitIntegrationId = safeTrim(toolData.integrationId);
  if (explicitIntegrationId !== '') {
    return explicitIntegrationId;
  }

  const systemToolKey = safeTrim(toolData.systemToolKey);
  if (systemToolKey !== '') {
    return 'aiTools';
  }

  const byName = inferCollectionIdFromToolName(getToolName(tool));
  if (byName !== '') {
    return byName;
  }

  return 'custom';
}

export function getToolCollectionMeta(tool) {
  const id = inferToolCollectionId(tool);
  return collectionMetaById[id] !== undefined ? collectionMetaById[id] : { id, name: id, logo: '/integrations/customTools.svg' };
}

export function getToolLogoUrl(tool) {
  const toolData = getToolData(tool);
  const explicitLogo = safeTrim(toolData.integrationLogo);
  if (explicitLogo !== '') {
    return explicitLogo;
  }
  const metaLogo = safeTrim(getToolCollectionMeta(tool).logo);
  if (metaLogo !== '') {
    return metaLogo;
  }
  return '/integrations/customTools.svg';
}

export function buildCollectionFilterOptions(tools, allLabel = 'All collections') {
  const rows = Array.isArray(tools) === true ? tools : [];
  const byId = new Map();

  for (let i = 0; i < rows.length; i = i + 1) {
    const meta = getToolCollectionMeta(rows[i]);
    const existing = byId.get(meta.id);
    if (existing === undefined) {
      byId.set(meta.id, { ...meta, count: 1 });
    } else {
      existing.count = existing.count + 1;
      byId.set(meta.id, existing);
    }
  }

  const options = Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
  return [
    {
      value: 'all',
      label: allLabel,
      description: '',
      icon: 'heroicons:squares-2x2-20-solid',
      imageUrl: ''
    },
    ...options.map((meta) => ({
      value: meta.id,
      label: meta.name,
      description: `${meta.count} tools`,
      icon: 'tabler:apps',
      imageUrl: meta.logo
    }))
  ];
}



